// Jungle Ruins — stage surface materials (texlib layers), on this stage's three reserved PATTERN slots.
//
// SURF maps your names to the slot ids; use them in layout.js as `pattern: SURF.<name>`. SURFACES lists what each
// slot holds: { slot, name, onWall?, onTop?, mat }.
//   templestone  carved Mesoamerican limestone & basalt temple flagstones: staggered ashlar courses, chiselled block
//                bevels, weathered pitting, and vibrant emerald-jade moss flourishing in the recessed mortar joints.
//                Paint mask: block colour tints the warm carved stone while the mossy seams keep their lush green;
//                vertical walls → mossystone.
//   mossystone   ancient mossy ruin masonry & sunken ritual court stone: coursed temple blocks overgrown with thick
//                velvety rainforest moss cushions and damp mineral streaks on both floors and retaining walls.
//   junglewood   weathered tropical hardwood (mahogany / teak) expedition decking & bridge planks: 22 cm planks with
//                staggered butt joints, rich dark grain, oxidized bronze bolt heads, and mossy seams.
export const SURF = { templestone: 52, mossystone: 53, junglewood: 54 };

const templestone = {
  detail: 0.55, scale: 2.6, tint: true, mask: true, alpha: false, mode: 1, sym: 3, hr: [-0.006, 0.003], ao: 0.48,
  prep: `ivec2 bc = wrp(ivec2(floor(P.x / 0.8), floor(P.y / 0.5)), ivec2(4, 6));
  uint bs = uint(bc.x * 6 + bc.y) * 73u;
  f[0] = FB(uv, ivec2(4), 4, 0.55, 5201u + bs); f[1] = FB(uv, ivec2(16), 3, 0.5, 5203u + bs);
  f[2] = FB(uv, ivec2(48), 2, 0.5, 5207u); f[3] = FB(uv, ivec2(12), 3, 0.55, 5209u);
  w[0] = WO(uv, ivec2(10), 0.85, 5211u); w[1] = WO(uv, ivec2(28), 0.9, 5213u);`,
  surf: /* glsl */`
  // Staggered carved temple flagstones (0.8 m x 0.5 m courses) with chiselled bevels & mossy mortar joints
  const float BW = 0.5, BL = 0.8;
  int row = int(floor(P.y / BW));
  int rowW = wrp(ivec2(row, 0), ivec2(6)).x;
  float stag = (row & 1) == 0 ? 0.0 : 0.4;
  float px = P.x + stag;
  int col = int(floor(px / BL));
  ivec2 id = wrp(ivec2(col, rowW), ivec2(4, 6));
  float dy = jd(P.y, BW);
  float dx = jd(px, BL);
  float dJoint = min(dy, dx) + 0.0035 * n[1];
  vec2 pr = edgeProf(dJoint, 0.006, 0.014, 0.0038, 0.006);
  float gap = pr.y;
  // Subtle carved step-fret / inner border groove on select blocks
  float hid = hf(id, 7u), hid2 = hf(id, 19u);
  float inGroove = smoothstep(0.005, 0.001, abs(min(dy, dx) - 0.055)) * step(0.45, hid);
  // Lush rainforest moss colonising the mortar joints and creeping onto block edges
  float mossCreep = smoothstep(0.055, 0.0, dJoint - 0.018 * (n[3] + 0.35 * n[0]));
  float mossPatch = smoothstep(0.38, 0.72, n[3] + 0.25 * n[0]) * (0.35 + 0.65 * (1.0 - smoothstep(0.0, 0.14, dJoint)));
  float moss = clamp(max(gap * 0.92, max(mossCreep * 0.82, mossPatch)), 0.0, 1.0);
  // Stone modulation (paint-masked so block colour drives the carved limestone/jade-grey tone)
  float stoneMod = 0.84 * (0.93 + 0.14 * hid) * (1.0 + 0.08 * n[0] + 0.05 * n[1] + 0.03 * n[2] - 0.08 * inGroove);
  vec3 mossDk = lin(vec3(0.14, 0.31, 0.15));
  vec3 mossLt = lin(vec3(0.28, 0.52, 0.22));
  vec3 mossCol = mix(mossDk, mossLt, clamp(0.5 + 0.45 * n[2] + 0.25 * n[1], 0.0, 1.0));
  vec3 mortarCol = lin(vec3(0.18, 0.22, 0.17)) * (0.9 + 0.15 * n[2]);
  vec3 nonTintCol = mix(mortarCol, mossCol, smoothstep(0.15, 0.65, moss));
  float nonTint = clamp(max(gap * 0.85, moss * 0.88), 0.0, 1.0);
  s.alb = nonTintCol * nonTint;
  s.a = (1.0 - nonTint) * stoneMod;
  s.h = pr.x + (0.0007 * (hid2 - 0.5) + 0.0005 * n[0] - 0.0012 * inGroove) * (1.0 - gap) + 0.0016 * moss;
  s.rough = mix(0.86 - 0.05 * hid + 0.04 * n[1], 0.94, moss);
  s.cav = mix(1.0 - 0.16 * inGroove, 0.48, gap * (1.0 - 0.5 * moss));`,
};

const mossystone = {
  detail: 0.52, scale: 2.8, tint: true, mask: true, alpha: false, mode: 1, sym: 3, hr: [-0.005, 0.004], ao: 0.45,
  prep: `f[0] = FB(uv, ivec2(3), 4, 0.55, 5301u); f[1] = FB(uv, ivec2(10, 5), 3, 0.5, 5303u);
  f[2] = FB(uv, ivec2(28, 12), 3, 0.5, 5307u); f[3] = FB(uv, ivec2(64), 2, 0.5, 5309u);
  w[0] = WO(uv, ivec2(9), 0.85, 5311u); w[1] = WO(uv, ivec2(24), 0.9, 5313u);`,
  surf: /* glsl */`
  // Ancient coursed ruin masonry + Voronoi stone blocks (c[0]) draped in velvety green moss cushions
  vec4 blk = c[0];
  float bEdge = blk.y - blk.x;
  float course = abs(fract(uv.y * 5.0 + 0.06 * n[0]) - 0.5);
  float seam = max(1.0 - smoothstep(0.0, 0.055, bEdge), 1.0 - smoothstep(0.0, 0.045, course));
  // Lush moss cushions thriving in the damp stone hollows and along horizontal ledges
  float mossField = n[0] * 0.55 + n[1] * 0.35 + 0.22 * seam;
  float moss = smoothstep(0.08, 0.48, mossField);
  float tuft = smoothstep(0.32, 0.72, n[2] + 0.3 * n[3]);
  float stoneMod = 0.80 * (1.0 + 0.09 * n[0] + 0.06 * n[1] + 0.04 * (fract(blk.z * 9.1) - 0.5) - 0.14 * seam);
  vec3 mossCol = mix(lin(vec3(0.13, 0.29, 0.14)), lin(vec3(0.30, 0.54, 0.21)), tuft) * (0.9 + 0.2 * n[3]);
  float nonTint = clamp(moss * 0.78 + seam * 0.35, 0.0, 1.0);
  s.alb = mossCol * nonTint;
  s.a = (1.0 - nonTint) * stoneMod;
  s.h = -0.0024 * seam + 0.0022 * moss * (0.6 + 0.4 * tuft) + 0.0004 * n[2];
  s.rough = mix(0.88 + 0.04 * seam, 0.95, moss);
  s.cav = 1.0 - 0.34 * seam * (1.0 - 0.5 * moss);`,
};

const junglewood = {
  detail: 0.5, scale: 2.4, tint: true, alpha: false, mode: 1, sym: 3, hr: [-0.006, 0.002], ao: 0.5,
  prep: `ivec2 pc = wrp(ivec2(floor(P.x / 1.2), floor(P.y / 0.2)), ivec2(2, 12));
  uint ps = uint(pc.x * 12 + pc.y) * 97u;
  f[0] = FB(uv, ivec2(2, 24), 3, 0.5, 5401u + ps); f[1] = FB(uv, ivec2(4, 64), 3, 0.5, 5403u + ps);
  f[2] = FB(uv, ivec2(16, 96), 2, 0.5, 5407u); w[0] = WO(uv, ivec2(8, 24), 0.75, 5411u);`,
  surf: /* glsl */`
  // 20 cm weathered tropical hardwood (mahogany / teak) planks along X, staggered butt joints every 1.2 m
  const float PW = 0.2, PL = 1.2;
  int row = int(floor(P.y / PW));
  int rowW = wrp(ivec2(row, 0), ivec2(12)).x;
  float stag = (row & 1) == 0 ? 0.0 : 0.6;
  float px = P.x + stag;
  int col = int(floor(px / PL));
  ivec2 id = wrp(ivec2(col, rowW), ivec2(2, 12));
  float dy = jd(P.y, PW);
  float dx = jd(px, PL);
  vec2 pr = edgeProf(min(dy, dx), 0.0035, 0.009, 0.0028, 0.005);
  float gap = pr.y;
  float hid = hf(id, 5u), hid2 = hf(id, 13u);
  float grain = 0.6 * n[0] + 0.4 * n[1] + 0.2 * sin(TAU * (P.y * 18.0 + 0.35 * n[0]));
  float damp = smoothstep(-0.1, 0.55, n[0] + 0.25 * (hid2 - 0.5));
  vec3 teak = mix(lin(vec3(0.48, 0.34, 0.23)), lin(vec3(0.60, 0.44, 0.30)), hid);
  vec3 mossTint = lin(vec3(0.32, 0.42, 0.25));
  vec3 wood = mix(teak, mossTint, 0.24 * damp) * (0.92 + 0.16 * hid2) * (1.0 + 0.11 * grain + 0.04 * n[2]);
  // Oxidized bronze bolt heads near plank butt ends
  float endX = min(fract(px / PL), 1.0 - fract(px / PL)) * PL;
  float py = (fract(P.y / PW) - 0.5) * PW;
  float bd = min(length(vec2(endX - 0.045, abs(py) - 0.055)), length(vec2(endX - 0.045, py)));
  float bolt = (1.0 - aa(0.008, bd)) * (1.0 - gap);
  vec3 seamCol = lin(vec3(0.16, 0.24, 0.15)) * (1.0 + 0.08 * n[2]);
  vec3 colOut = mix(wood, lin(vec3(0.46, 0.38, 0.20)), bolt * 0.85);
  s.alb = mix(colOut, seamCol, gap);
  s.h = pr.x + (0.00045 * grain + 0.00015 * n[2] - 0.0012 * bolt) * (1.0 - gap);
  s.rough = mix(0.82 - 0.05 * damp - 0.22 * bolt, 0.94, gap);
  s.metal = 0.6 * bolt * (1.0 - gap);
  s.cav = mix(1.0 - 0.22 * bolt, 0.42, gap);`,
};

export const SURFACES = [
  { slot: 52, name: 'templestone', onWall: 53, mat: templestone },
  { slot: 53, name: 'mossystone', mat: mossystone },
  { slot: 54, name: 'junglewood', mat: junglewood },
];
